import {
  Description as DescriptionIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import jsPDF from "jspdf";
import { useCallback, useEffect, useRef, useState } from "react";
import licencaFooterImg from "../../assets/images/licenca-footer.png";
import licencaHeaderImg from "../../assets/images/licenca-header.png";
import api from "../../services/api";
import pdfLayoutService from "../../services/pdfLayout";
import { ATIVIDADE_POR_GRUPO, GRUPO_ATIVIDADE_OPTIONS, modalidadeEnum } from "../../utils/constants";

const TEXTO_OBS_PADRAO_CHECKLIST =
  "OBS: Durante a análise do processo, poderão ser solicitadas outras informações e/ou estudos específicos, caso o setor técnico julgue necessário.";

function Checklist() {
  const theme = useTheme();
  const modalContentRef = useRef(null);
  const [tiposLicenca, setTiposLicenca] = useState([]);
  const [tipoInputValue, setTipoInputValue] = useState("");
  const [error, setError] = useState("");
  const [itensChecklist, setItensChecklist] = useState([]);
  const [checklistNaoEncontrado, setChecklistNaoEncontrado] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [headerImgData, setHeaderImgData] = useState(null);
  const [footerImgData, setFooterImgData] = useState(null);

  const [formData, setFormData] = useState({
    tipoLicenca: null,
    modalidade: "",
    grupoAtividade: "",
    atividadeEmpreendimento: "",
  });

  const carregarPdfLayout = useCallback(async (tipoLicencaId) => {
    if (!tipoLicencaId) {
      setHeaderImgData(null);
      setFooterImgData(null);
      return;
    }
    try {
      const res = await pdfLayoutService.obterPorTipoLicenca(tipoLicencaId);
      const layoutId = res.data?.data;
      if (!layoutId) {
        setHeaderImgData(null);
        setFooterImgData(null);
        return;
      }

      const toDataUrl = (blob) =>
        new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });

      const [headerRes, footerRes] = await Promise.all([
        pdfLayoutService.getHeaderImage(layoutId).catch(() => null),
        pdfLayoutService.getFooterImage(layoutId).catch(() => null),
      ]);

      setHeaderImgData(headerRes ? await toDataUrl(headerRes.data) : null);
      setFooterImgData(footerRes ? await toDataUrl(footerRes.data) : null);
    } catch {
      setHeaderImgData(null);
      setFooterImgData(null);
    }
  }, []);

  useEffect(() => {
    carregarPdfLayout(formData.tipoLicenca?.id);
  }, [formData.tipoLicenca, carregarPdfLayout]);

  const carregarTiposLicenca = async (nome) => {
    try {
      const response = await api.get("TipoLicenca/GetByNomeAsync", {
        params: { nome },
      });

      setTiposLicenca(response.data || []);
    } catch (error) {
      setError(
        "Não foi possível carregar os tipos de licença. Por favor, tente novamente mais tarde.",
      );
    }
  };

  const buscarDocumentosDoChecklist = async (
    tipoLicencaId,
    grupoAtividadeParam,
    atividadeEmpreendimentoParam,
    modalidadeParam,
  ) => {
    if (
      !tipoLicencaId ||
      grupoAtividadeParam === "" || grupoAtividadeParam === null || grupoAtividadeParam === undefined ||
      atividadeEmpreendimentoParam === "" || atividadeEmpreendimentoParam === null || atividadeEmpreendimentoParam === undefined ||
      modalidadeParam === "" || modalidadeParam === null || modalidadeParam === undefined
    ) {
      setItensChecklist([]);
      setChecklistNaoEncontrado(false);
      return;
    }

    try {
      const response = await api.get(`/Checklist/filtro`, {
        params: {
          tipoLicencaId,
          grupoAtividade: parseInt(grupoAtividadeParam, 10),
          atividadeEmpreendimento: parseInt(atividadeEmpreendimentoParam, 10),
          modalidade: parseInt(modalidadeParam, 10),
        },
      });

      const itensList = response.data?.itensChecklist || [];
      setChecklistNaoEncontrado(itensList.length === 0);

      const itens = itensList.map((item) => ({
        id: item.id,
        descricao: item.descricao,
        observacoes: item.observacoes,
        obrigatorio: item.obrigatorio,
      }));
      setItensChecklist(itens);
    } catch (error) {
      setItensChecklist([]);
      setChecklistNaoEncontrado(true);
    }
  };

  useEffect(() => {
    carregarTiposLicenca(tiposLicenca);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    buscarDocumentosDoChecklist(
      formData.tipoLicenca?.id,
      formData.grupoAtividade,
      formData.atividadeEmpreendimento,
      formData.modalidade,
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tipoLicenca, formData.modalidade, formData.grupoAtividade, formData.atividadeEmpreendimento]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "grupoAtividade") {
      setFormData((prev) => ({ ...prev, grupoAtividade: value, atividadeEmpreendimento: "" }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getModalidadeLabel = () => {
    const modalidade = modalidadeEnum.find(
      (opt) => opt.value.toString() === formData.modalidade.toString(),
    );
    return modalidade?.label || "";
  };

  const getGrupoAtividadeLabel = () => {
    const grupo = GRUPO_ATIVIDADE_OPTIONS.find(
      (opt) => opt.value.toString() === formData.grupoAtividade.toString(),
    );
    return grupo?.label || "";
  };

  const getAtividadeEmpreendimentoLabel = () => {
    const atividadesDoGrupo = ATIVIDADE_POR_GRUPO[formData.grupoAtividade] || [];
    const atividade = atividadesDoGrupo.find(
      (opt) => opt.value.toString() === formData.atividadeEmpreendimento.toString(),
    );
    return atividade?.label || "";
  };

  const handleBaixarPdf = async () => {
    try {
      setGerandoPdf(true);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      const tipoLicenca =
        formData.tipoLicenca?.nome || formData.tipoLicenca?.Nome || "Licença";
      const atividade = getGrupoAtividadeLabel();

      const pageWidth = 210;
      const pageHeight = 297;
      const headerHeight = 30;
      const footerHeight = 25;
      const margin = 20;
      const lineHeight = 7;
      const centerX = pageWidth / 2;
      const contentStartY = headerHeight + 10;
      const contentEndY = pageHeight - footerHeight - 10;
      let yPosition = contentStartY;

      const headerSrc = headerImgData || licencaHeaderImg;
      const footerSrc = footerImgData || licencaFooterImg;

      const addHeaderFooter = () => {
        pdf.addImage(headerSrc, "PNG", 0, 0, pageWidth, headerHeight);
        pdf.addImage(
          footerSrc,
          "PNG",
          0,
          pageHeight - footerHeight,
          pageWidth,
          footerHeight,
        );
      };

      const printJustified = (lines, x, maxWidth) => {
        lines.forEach((line, i) => {
          const isLastLine = i === lines.length - 1;
          if (isLastLine) {
            pdf.text(line, x, yPosition);
          } else {
            const words = line.split(" ");
            if (words.length <= 1) {
              pdf.text(line, x, yPosition);
            } else {
              const totalWordsWidth = words.reduce(
                (sum, w) => sum + pdf.getTextWidth(w),
                0,
              );
              const gap = (maxWidth - totalWordsWidth) / (words.length - 1);
              let curX = x;
              words.forEach((word) => {
                pdf.text(word, curX, yPosition);
                curX += pdf.getTextWidth(word) + gap;
              });
            }
          }
          yPosition += lineHeight;
        });
      };

      addHeaderFooter();

      const checkAddPage = (requiredSpace = lineHeight) => {
        if (yPosition + requiredSpace > contentEndY) {
          pdf.addPage();
          addHeaderFooter();
          yPosition = contentStartY;
          return true;
        }
        return false;
      };

      pdf.setFontSize(14);
      pdf.setFont(undefined, "bold");
      pdf.text(atividade, centerX, yPosition, { align: "center" });
      yPosition += lineHeight * 1.5;

      pdf.setFontSize(12);
      pdf.text(tipoLicenca, centerX, yPosition, { align: "center" });
      yPosition += lineHeight * 2;

      pdf.setFontSize(10);
      pdf.setFont(undefined, "normal");
      const textoDescritivo =
        "Concedida na fase preliminar do planejamento do empreendimento ou atividade, aprovando sua " +
        "localização e concepção, atestando a viabilidade ambiental e estabelecendo os requisitos básicos " +
        "e condicionantes a serem atendidos nas próximas fases de sua implementação. NÃO " +
        "AUTORIZA O INÍCIO DAS OBRAS NEM QUALQUER OUTRO TIPO DE ATIVIDADE.";

      const textoLines = pdf.splitTextToSize(
        textoDescritivo,
        pageWidth - margin * 2,
      );
      printJustified(textoLines, margin, pageWidth - margin * 2);
      yPosition += lineHeight;

      pdf.setFontSize(12);
      pdf.setFont(undefined, "bold");
      pdf.text("DOCUMENTAÇÃO NECESSÁRIA", centerX, yPosition, {
        align: "center",
      });
      yPosition += lineHeight * 2;

      pdf.setFontSize(10);
      itensChecklist.forEach((item, index) => {
        checkAddPage(lineHeight * 4);

        pdf.setFont(undefined, "bold");
        const nomeTexto = `${index + 1}. ${item.descricao}`.toUpperCase();
        const nomeLines = pdf.splitTextToSize(
          nomeTexto,
          pageWidth - margin * 2,
        );
        nomeLines.forEach((line, i) => {
          checkAddPage();
          const isLastLine = i === nomeLines.length - 1;
          if (isLastLine || nomeLines.length === 1) {
            pdf.text(line, margin, yPosition);
          } else {
            const words = line.split(" ");
            if (words.length <= 1) {
              pdf.text(line, margin, yPosition);
            } else {
              const totalWordsWidth = words.reduce(
                (sum, w) => sum + pdf.getTextWidth(w),
                0,
              );
              const gap = (pageWidth - margin * 2 - totalWordsWidth) / (words.length - 1);
              let curX = margin;
              words.forEach((word) => {
                pdf.text(word, curX, yPosition);
                curX += pdf.getTextWidth(word) + gap;
              });
            }
          }
          yPosition += lineHeight;
        });

        if (item.observacoes) {
          pdf.setFont(undefined, "normal");
          const observacoesNormalizadas = item.observacoes.replace(/[\r\n]+/g, " ").replace(/\s+/g, " ").trim();
          const descTexto = `Descrição: ${observacoesNormalizadas}`;
          const descLines = pdf.splitTextToSize(
            descTexto,
            pageWidth - margin * 2,
          );
          descLines.forEach((line, i) => {
            checkAddPage();
            const isLastLine = i === descLines.length - 1;
            if (isLastLine) {
              pdf.text(line, margin, yPosition);
            } else {
              const words = line.split(" ");
              if (words.length <= 1) {
                pdf.text(line, margin, yPosition);
              } else {
                const totalWordsWidth = words.reduce(
                  (sum, w) => sum + pdf.getTextWidth(w),
                  0,
                );
                const gap = (pageWidth - margin * 2 - totalWordsWidth) / (words.length - 1);
                let curX = margin;
                words.forEach((word) => {
                  pdf.text(word, curX, yPosition);
                  curX += pdf.getTextWidth(word) + gap;
                });
              }
            }
            yPosition += lineHeight;
          });
        }

        pdf.setFont(undefined, "bold");
        checkAddPage();
        pdf.text(
          item.obrigatorio ? "Obrigatório" : "Opcional",
          margin,
          yPosition,
        );
        yPosition += lineHeight * 1.5;
      });

      checkAddPage(lineHeight * 3);
      yPosition += lineHeight;

      pdf.setFontSize(10);
      pdf.setFont(undefined, "normal");
      const obsLines = pdf.splitTextToSize(
        TEXTO_OBS_PADRAO_CHECKLIST,
        pageWidth - margin * 2,
      );
      printJustified(obsLines, margin, pageWidth - margin * 2);

      const dataAtual = new Date().toISOString().split("T")[0];
      const nomeArquivo = `Documentos-${tipoLicenca.replace(/[^a-zA-Z0-9]/g, "_")}-${dataAtual}.pdf`;

      pdf.save(nomeArquivo);
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      setError("Erro ao gerar o PDF. Tente novamente.");
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ mt: { xs: 3, md: 6 }, mb: { xs: 4, md: 8 } }}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
            bgcolor: "background.paper",
            position: "relative",
            overflow: "hidden",
            "&:before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "8px",
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
            },
          }}
        >
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              fontSize: { xs: "1.5rem", sm: "2rem" },
              mb: 5,
              textAlign: "center",
            }}
          >
            Simulador de Checklist
          </Typography>

          {error && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                bgcolor: alpha(theme.palette.error.main, 0.08),
                borderRadius: 1,
              }}
            >
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={tiposLicenca}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : option?.nome ||
                      option?.Nome ||
                      option?.codigo ||
                      option?.Codigo ||
                      ""
                }
                value={formData.tipoLicenca}
                isOptionEqualToValue={(option, value) =>
                  option?.id === value?.id
                }
                onChange={(_, newValue) =>
                  setFormData({ ...formData, tipoLicenca: newValue })
                }
                inputValue={tipoInputValue}
                onInputChange={(_, newInput) => setTipoInputValue(newInput)}
                noOptionsText={
                  tipoInputValue && tipoInputValue.trim().length >= 3
                    ? "Nenhum tipo encontrado"
                    : "Digite 3 ou mais letras"
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de Licença"
                    placeholder="Ex: Licença Prévia"
                    required
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="modalidade-label">Modalidade</InputLabel>
                <Select
                  labelId="modalidade-label"
                  id="modalidade-select"
                  name="modalidade"
                  value={formData.modalidade}
                  onChange={handleChange}
                  label="Modalidade"
                >
                  <MenuItem value="">
                    <em>Selecione uma modalidade</em>
                  </MenuItem>
                  {modalidadeEnum.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel id="grupo-atividade-label">Grupo de Atividade</InputLabel>
                <Select
                  labelId="grupo-atividade-label"
                  id="grupo-atividade-select"
                  name="grupoAtividade"
                  value={formData.grupoAtividade}
                  onChange={handleChange}
                  label="Grupo de Atividade"
                >
                  <MenuItem value="">
                    <em>Selecione um grupo</em>
                  </MenuItem>
                  {GRUPO_ATIVIDADE_OPTIONS.map((g) => (
                    <MenuItem key={g.value} value={g.value}>
                      {g.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel id="atividade-empreendimento-label">Atividade do Empreendimento</InputLabel>
                <Select
                  labelId="atividade-empreendimento-label"
                  id="atividade-empreendimento-select"
                  name="atividadeEmpreendimento"
                  value={formData.atividadeEmpreendimento}
                  onChange={handleChange}
                  label="Atividade do Empreendimento"
                  disabled={formData.grupoAtividade === ""}
                >
                  <MenuItem value="">
                    <em>
                      {formData.grupoAtividade === ""
                        ? "Selecione um grupo de atividade primeiro"
                        : "Selecione uma atividade"}
                    </em>
                  </MenuItem>
                  {(ATIVIDADE_POR_GRUPO[formData.grupoAtividade] || []).map((a) => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {formData.tipoLicenca &&
          formData.modalidade !== "" &&
          formData.grupoAtividade !== "" &&
          formData.atividadeEmpreendimento !== "" && (
          <Box sx={{ mt: 4 }}>
            {checklistNaoEncontrado ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.warning.light, 0.15),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                  borderRadius: 2,
                }}
              >
                <Typography color="warning.dark" fontWeight={500} align="center">
                  Não há checklist cadastrado para a combinação de filtros selecionada.
                </Typography>
              </Box>
            ) : (
              <Paper
                elevation={1}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  bgcolor: "background.paper",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 4,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                }}
              >
                <Box
                  sx={{ display: "flex", gap: 3, flex: 1, alignItems: "flex-start", flexWrap: "wrap" }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Tipo de Licença
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {formData.tipoLicenca?.nome ||
                        formData.tipoLicenca?.Nome ||
                        "—"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Modalidade
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {getModalidadeLabel() || "—"}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Grupo de Atividade
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {getGrupoAtividadeLabel() || "—"}
                    </Typography>
                  </Box>

                  <Box sx={{ maxWidth: 300 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Atividade do Empreendimento
                    </Typography>
                    <Typography variant="body2" fontWeight="600">
                      {getAtividadeEmpreendimentoLabel() || "—"}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  variant="contained"
                  color="success"
                  onClick={() => setOpenModal(true)}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                    px: 3,
                    whiteSpace: "nowrap",
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Ver Documentos
                </Button>
              </Paper>
            )}
          </Box>
        )}
      </Box>

      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: "calc(100% - 16px)", sm: "90vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: theme.palette.success.main,
            pb: 2,
            fontSize: "1.3rem",
          }}
        >
          Lista de Documentos ({itensChecklist.length})
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pb: 3 }} ref={modalContentRef}>
          {itensChecklist.length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {itensChecklist.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    p: 2,
                    borderLeft: `4px solid ${theme.palette.success.main}`,
                    bgcolor: alpha(theme.palette.success.light, 0.08),
                    borderRadius: "0 8px 8px 0",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 28,
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: theme.palette.success.main,
                        color: "success.contrastText",
                        flexShrink: 0,
                        mt: 0.3,
                      }}
                    >
                      <DescriptionIcon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight="700"
                        sx={{ mb: 0.5 }}
                      >
                        {item.descricao}
                      </Typography>
                      {item.observacoes && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1, lineHeight: 1.4 }}
                        >
                          {item.observacoes}
                        </Typography>
                      )}
                      <Chip
                        label={item.obrigatorio ? "Obrigatório" : "Opcional"}
                        size="small"
                        color={item.obrigatorio ? "error" : "default"}
                        variant="outlined"
                        sx={{ height: 24 }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : checklistNaoEncontrado ? (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              Não há checklist cadastrado para este tipo de licença e atividade.
            </Typography>
          ) : (
            <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
              Nenhum documento encontrado
            </Typography>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
            "& > button": { width: { xs: "100%", sm: "auto" } },
          }}
        >
          <Button
            onClick={() => setOpenModal(false)}
            sx={{ textTransform: "none" }}
          >
            Fechar
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleBaixarPdf}
            disabled={gerandoPdf || itensChecklist.length === 0}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {gerandoPdf ? "Gerando..." : "Baixar PDF"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Checklist;
